import React, {useEffect, useRef, useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
    useMultiStyleConfig,
    useTheme,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter
} from '@chakra-ui/react'
import {IconButton, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CustomClose, LeftArrow} from '../icons'

// Tracks open drawers for nested detection: [{id, onClose}]
const drawerStack = []

const closeAllDrawers = () => {
    // Close from top (innermost) to bottom (outermost)
    ;[...drawerStack].reverse().forEach(({onClose}) => onClose())
}

// Mobile height map keyed by size prop
const MOBILE_SIZE_MAP = {
    hug: {height: 'auto', minHeight: 'unset'},
    min: {height: 'auto', minHeight: '300px'},
    '85vh': {height: '85vh', minHeight: '85vh'},
    full: {height: '100dvh', minHeight: '100dvh'}
}

const WebDrawer = ({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'hug',
    showCloseButton = true,
    showBackButton = false,
    closeOnOverlayClick = true,
    id,
    backButton,
    onBack,
    headerInContent = false,
    noBodyStyle = false,
    titleTextStyle,
    hideHeaderBorderBottom = false,
    titleSx,
    finalFocusRef
}) => {
    const drawerStyles = useMultiStyleConfig('Drawer', {variant: 'webDrawer'})
    const theme = useTheme()
    const lgPx = parseFloat(theme.breakpoints?.lg ?? '62em') * 16
    const [placement, setPlacement] = useState(() => {
        if (typeof window === 'undefined') return 'bottom'
        return window.innerWidth >= lgPx ? 'right' : 'bottom'
    })
    useEffect(() => {
        const update = () => setPlacement(window.innerWidth >= lgPx ? 'right' : 'bottom')
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [lgPx])
    const bodyRef = useRef(null)
    const [isBodyOverflowing, setIsBodyOverflowing] = useState(false)
    const [isNested, setIsNested] = useState(false)
    const stackId = useRef(id || Math.random().toString(36).slice(2))

    // Overflow detection for header/footer shadows
    const checkOverflow = useCallback(() => {
        if (!bodyRef.current) return
        setIsBodyOverflowing(bodyRef.current.scrollHeight > bodyRef.current.clientHeight)
    }, [])

    useEffect(() => {
        if (!isOpen) {
            setIsBodyOverflowing(false)
            return
        }
        const timer = setTimeout(checkOverflow, 300)
        const el = bodyRef.current
        if (!el) return () => clearTimeout(timer)
        const ro = new ResizeObserver(checkOverflow)
        ro.observe(el)
        // Watch for child content changes (e.g. loading → loaded)
        const mo = new MutationObserver(checkOverflow)
        mo.observe(el, {childList: true, subtree: true})
        return () => {
            clearTimeout(timer)
            ro.disconnect()
            mo.disconnect()
        }
    }, [isOpen, checkOverflow])

    // Nested drawer tracking
    useEffect(() => {
        if (isOpen) {
            if (!drawerStack.find((e) => e.id === stackId.current)) {
                drawerStack.push({id: stackId.current, onClose})
            }
            setIsNested(drawerStack.findIndex((e) => e.id === stackId.current) > 0)
        } else {
            setIsNested(false)
        }
        return () => {
            const idx = drawerStack.findIndex((e) => e.id === stackId.current)
            if (idx !== -1) drawerStack.splice(idx, 1)
        }
    }, [isOpen, onClose])

    const _showBackButton = showBackButton || !!(backButton && onBack)
    // The visible close affordances should dismiss the full drawer stack.
    const handleClose = closeAllDrawers

    // Responsive size styles for DrawerContent
    const mobileSize = MOBILE_SIZE_MAP[size] || MOBILE_SIZE_MAP.hug
    const contentSizeStyles = {
        height: {base: mobileSize.height, lg: '100%'},
        minHeight: {base: mobileSize.minHeight, lg: 'unset'},
        maxHeight: {base: '100dvh', lg: 'unset'},
        borderTopRadius: 0
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={handleClose}
            placement={placement}
            closeOnOverlayClick={closeOnOverlayClick}
            variant="webDrawer"
            finalFocusRef={finalFocusRef}
        >
            {isNested ? null : <DrawerOverlay />}

            <DrawerContent
                sx={{
                    ...drawerStyles.dialog,
                    ...contentSizeStyles
                }}
            >
                {/* Header */}
                <DrawerHeader
                    sx={{
                        ...drawerStyles.header,
                        ...((!title || headerInContent) && {minH: '56px'}),
                        ...(headerInContent && {borderBottom: 'none'}),
                        ...(hideHeaderBorderBottom && {borderBottom: 'none'}),
                        boxShadow: isBodyOverflowing ? 'sm' : 'unset',
                        transition: 'box-shadow 150ms ease'
                    }}
                >
                    {_showBackButton && (
                        <IconButton
                            variant="unstyled"
                            aria-label="Go back"
                            icon={backButton || <LeftArrow w="24px" h="24px" />}
                            onClick={onBack || onClose}
                            sx={{
                                ...drawerStyles.backButton
                            }}
                        />
                    )}
                    {title && !headerInContent && (
                        <Text
                            as="span"
                            sx={{
                                ...drawerStyles.headerText,
                                flex: 1,
                                noOfLines: 2,
                                pr: showCloseButton ? 8 : 0,
                                ...(titleTextStyle && {textStyle: titleTextStyle}),
                                ...(titleSx && titleSx)
                            }}
                        >
                            {title}
                        </Text>
                    )}
                    {showCloseButton && (
                        <IconButton
                            variant="unstyled"
                            aria-label="Close"
                            icon={<CustomClose />}
                            onClick={handleClose}
                            sx={drawerStyles.closeButton}
                            data-testid="drawer-close-button"
                        />
                    )}
                </DrawerHeader>

                {/* Body */}
                <DrawerBody
                    ref={bodyRef}
                    sx={noBodyStyle ? {
                        paddingTop: {base: 0, lg: 0},
                        paddingRight: {base: 0, lg: 0},
                        paddingBottom: {base: 0, lg: 0},
                        paddingLeft: {base: 0, lg: 0}
                    } : {
                        ...drawerStyles.body,
                        '&::-webkit-scrollbar': {width: '5px'},
                        '&::-webkit-scrollbar-thumb': {
                            bg: 'webTheme.strokeSecondary',
                            borderRadius: 'full'
                        },
                        '&::-webkit-scrollbar-track': {bg: 'transparent'}
                    }}
                >
                    {headerInContent && title && (
                        <Text sx={{...drawerStyles.headerText}}>
                            {title}
                        </Text>
                    )}
                    {children}
                </DrawerBody>

                {/* Footer */}
                {footer && (
                    <DrawerFooter
                        sx={{
                            ...drawerStyles.footer,
                            flexWrap: 'wrap',
                            boxShadow: isBodyOverflowing
                                ? '0 -2px 8px rgba(0,0,0,0.06)'
                                : 'none',
                            transition: 'box-shadow 150ms ease'
                        }}
                    >
                        {footer}
                    </DrawerFooter>
                )}
            </DrawerContent>
        </Drawer>
    )
}

WebDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.node,
    size: PropTypes.oneOf(['hug', 'min', '85vh', 'full']),
    showCloseButton: PropTypes.bool,
    closeOnOverlayClick: PropTypes.bool,
    id: PropTypes.string,
    backButton: PropTypes.node,
    onBack: PropTypes.func,
    headerInContent: PropTypes.bool,
    noBodyStyle: PropTypes.bool,
    titleTextStyle: PropTypes.string,
    titleSx: PropTypes.object,
    hideHeaderBorderBottom: PropTypes.bool,
    hideHeaderBorderBottom: PropTypes.bool,
    finalFocusRef: PropTypes.shape({current: PropTypes.any})
}

export default WebDrawer
